import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { PriceRangesI, AgglomerationsPriceRanges } from "@/types/base";
import {
  getPriceRanges,
  getAgglomerationsPriceRanges,
  getSnapshotPriceRanges,
} from "@/api/base";
import { useSnapshotsStore } from "@/store/snapshots";
import { useTariffLayer } from "@/store/mode";
import { getTariffModeConfig } from "@/utils/tariffModeConfig";

interface PriceRangesContextValue {
  priceRanges: PriceRangesI[];
  priceRangesLoading: boolean;
  agglomerationsPriceRanges: AgglomerationsPriceRanges | null;
  reloadPriceRanges: (ignoreLoader?: boolean) => Promise<void>;
}

const PriceRangesContext = createContext<PriceRangesContextValue | undefined>(
  undefined
);

export const PriceRangesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const tariffLayer = useTariffLayer((s) => s.tariffLayer);
  const activeSnapshotTimestamp = useSnapshotsStore(
    (s) => s.activeSnapshotTimestamp
  );
  const { useSnapshots } = getTariffModeConfig(tariffLayer);
  const priceRangesSnapshotTimestamp = useSnapshots
    ? activeSnapshotTimestamp
    : null;

  const [livePriceRanges, setLivePriceRanges] = useState<PriceRangesI[]>([]);
  const [snapshotPriceRanges, setSnapshotPriceRanges] = useState<
    Record<string, PriceRangesI[]>
  >({});
  const [
    agglomerationsPriceRanges,
    setAgglomerationsPriceRanges,
  ] = useState<AgglomerationsPriceRanges | null>(null);
  const [priceRangesLoading, setPriceRangesLoading] = useState(true);

  const priceRanges = useMemo(() => {
    if (!priceRangesSnapshotTimestamp) return livePriceRanges;
    return snapshotPriceRanges[priceRangesSnapshotTimestamp] ?? livePriceRanges;
  }, [livePriceRanges, priceRangesSnapshotTimestamp, snapshotPriceRanges]);

  const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const loadData = async (ignoreLoader?: boolean) => {
    if (!ignoreLoader) setPriceRangesLoading(true);

    const tryOnce = async () => {
      const [res, aggRes, snapshotRes] = await Promise.all([
        getPriceRanges(),
        getAgglomerationsPriceRanges(),
        getSnapshotPriceRanges(),
      ]);
      setLivePriceRanges(res);
      setAgglomerationsPriceRanges(aggRes);
      setSnapshotPriceRanges(snapshotRes);
    };

    try {
      await tryOnce();
    } catch (e1) {
      console.log("getPriceRanges failed, retrying in 1s...", e1);
      try {
        await sleep(1000);
        await tryOnce();
      } catch (e2) {
        console.log("getPriceRanges failed after retry", e2);
      }
    } finally {
      setPriceRangesLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const data = setInterval(() => {
      loadData(true);
    }, 60000);

    return () => {
      clearInterval(data);
    };
  }, []);

  return (
    <PriceRangesContext.Provider
      value={{
        priceRanges,
        priceRangesLoading,
        agglomerationsPriceRanges,
        reloadPriceRanges: loadData,
      }}
    >
      {children}
    </PriceRangesContext.Provider>
  );
};

export const usePriceRangesDataContext = (): PriceRangesContextValue => {
  const ctx = useContext(PriceRangesContext);
  if (!ctx) {
    throw new Error("usePriceRanges must be used within PriceRangesProvider");
  }
  return ctx;
};
